import { Injectable } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';

export interface BreadcrumbItem {
  label: string;
  url?: string;
  action?: () => void;
  /** true = label is an i18n key; false = already-resolved entity name */
  isKey?: boolean;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private routeItems: BreadcrumbItem[] = [];
  private entityLabel: string | null = null;
  private modalItems: BreadcrumbItem[] = [];

  private readonly _items$ = new BehaviorSubject<BreadcrumbItem[]>([]);
  readonly items$ = this._items$.asObservable();

  /** The last breadcrumb item — used as the page h1 title. */
  readonly pageTitle$ = this._items$.pipe(
    map((items) => (items.length ? items[items.length - 1] : null)),
  );

  /** Called by AppBreadcrumbComponent on every NavigationEnd. Resets entity + modal stack. */
  setRouteItems(items: BreadcrumbItem[]): void {
    this.routeItems = items;
    this.entityLabel = null;
    this.modalItems = [];
    this.emit();
  }

  /**
   * Override the last route item's label with a resolved entity name.
   * Called from detail pages once async data is available
   * (replaces the old LayoutService.setCustomBreadcrumb() hack).
   */
  setEntityLabel(label: string): void {
    this.entityLabel = label;
    this.emit();
  }

  /**
   * Push a context item from a modal/panel component.
   * Call pushModal() in ngOnInit and popModal() in ngOnDestroy.
   */
  pushModal(item: BreadcrumbItem): void {
    this.modalItems = [...this.modalItems, item];
    this.emit();
  }

  popModal(): void {
    this.modalItems = this.modalItems.slice(0, -1);
    this.emit();
  }

  private emit(): void {
    const resolved =
      this.entityLabel && this.routeItems.length
        ? [
            ...this.routeItems.slice(0, -1),
            { ...this.routeItems[this.routeItems.length - 1], label: this.entityLabel, isKey: false },
          ]
        : this.routeItems;

    this._items$.next([...resolved, ...this.modalItems]);
  }
}
